import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { contactInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Submit to backend API
    axios.post(`${BACKEND_URL}/api/contact`, formData)
      .then(response => {
        console.log('Contact inquiry submitted:', response.data);
        toast.success('Message Sent Successfully!', {
          description: 'We will get back to you soon.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      })
      .catch(error => {
        console.error('Error submitting contact form:', error);
        toast.error('Failed to send message. Please try again.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hello, I'm interested in RK ROTOARC products");
    const phone = `91${contactInfo.whatsapp}`;
    
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile, use app intent
      window.location.href = `whatsapp://send?phone=${phone}&text=${message}`;
    } else {
      // On desktop, use web WhatsApp
      window.open(`https://web.whatsapp.com/send?phone=${phone}&text=${message}`, '_blank');
    }
  };

  return (
    <section id="contact" className="py-20 bg-zinc-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(234,88,12,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(234,88,12,0.08),transparent_50%)]"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-orange-500 text-sm font-bold tracking-wider uppercase">Get In Touch</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Contact <span className="text-orange-500">RK ROTOARC</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Have questions about our products? Our team is ready to assist you with all your welding equipment needs
          </p>
          <div className="w-24 h-1 bg-orange-600 mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information Cards */}
          <div className="space-y-6">
            <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800 hover:border-orange-600/50 transition-colors group">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-orange-600/20 flex items-center justify-center mb-4 group-hover:bg-orange-600/30 transition-colors">
                  <Phone className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Phone</h3>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="text-zinc-400 hover:text-orange-500 transition-colors"
                >
                  +91 {contactInfo.phone}
                </a>
                <p className="text-zinc-500 text-sm mt-2">Mon-Sat, 9:00 AM - 6:00 PM</p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800 hover:border-orange-600/50 transition-colors group">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-orange-600/20 flex items-center justify-center mb-4 group-hover:bg-orange-600/30 transition-colors">
                  <Mail className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Email</h3>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="text-zinc-400 hover:text-orange-500 transition-colors break-all"
                >
                  {contactInfo.email}
                </a>
                <p className="text-zinc-500 text-sm mt-2">24-48 hours response time</p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800 hover:border-orange-600/50 transition-colors group">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-orange-600/20 flex items-center justify-center mb-4 group-hover:bg-orange-600/30 transition-colors">
                  <MapPin className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Location</h3>
                <p className="text-zinc-400">{contactInfo.address}</p>
              </CardContent>
            </Card>

            {/* WhatsApp CTA */}
            <Card className="bg-gradient-to-br from-green-600/20 to-green-700/10 backdrop-blur-sm border-green-600/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MessageSquare className="w-8 h-8 text-green-500" />
                  <div>
                    <h3 className="text-lg font-bold text-white">Quick Response</h3>
                    <p className="text-zinc-400 text-sm">Chat with us on WhatsApp</p>
                  </div>
                </div>
                <Button 
                  onClick={handleWhatsApp}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <MessageSquare className="mr-2 w-4 h-4" />
                  WhatsApp Us
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Send Us a Message</h3>
                  <p className="text-zinc-400">Fill out the form and we'll get back to you as soon as possible</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="contact-name" className="text-zinc-300">Full Name *</Label>
                      <Input
                        id="contact-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-email" className="text-zinc-300">Email Address *</Label>
                      <Input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="contact-phone" className="text-zinc-300">Phone Number *</Label>
                      <Input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-subject" className="text-zinc-300">Subject *</Label>
                      <Input
                        id="contact-subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                        placeholder="Product Inquiry"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contact-message" className="text-zinc-300">Message *</Label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg group"
                  >
                    <Send className="mr-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;